import { useEffect, useState } from "react";

/**
 * client/src/lib/useModuleStatus.ts — which optional modules this deployment actually serves.
 *
 * WHY IT IS ITS OWN MODULE (S03, 2026-09-22). This probe was written inline inside `ui/AppShell`,
 * where it decided whether the bar shows "Your briefs" and "CRM". The app home page at `/app` has
 * to make the SAME decision about the SAME two routes — and a second copy of a rule that reads
 * `=== true`, defaults to false on every failure, and distinguishes two independent flags is a
 * second place for one of those three details to be got wrong. So the probe moved here and both
 * surfaces ask it. Nothing about its behaviour changed in the move.
 *
 * SERVER-GATED, NOT BUILD-GATED. `VALUBRIEF_CRM_NEXT_ENABLED` is read per request by the API, so a
 * build-time constant would be a second source of truth that goes stale the moment the flag is
 * flipped on the deploy without a rebuild. `GET /api/next/crm/status` is the one route in that
 * module which answers whether the module exists — every OTHER crm route 404s when the flag is
 * off, and a 404 cannot be told apart from a stale build or a typo'd path.
 *
 * TWO FLAGS, NOT ONE, AND THEY ARE INDEPENDENT. The shelf (`/briefs`) ships on while
 * `VALUBRIEF_CRM_NEXT_ENABLED` stays off pending the cutover ADR-0004 leaves unscheduled, so the
 * common deployment is shelf-on / CRM-off. A caller that read only `enabled` would hide the one
 * link that works.
 *
 * ANY FAILURE MEANS OFF. Not signed in, API down, flag off, older server with no such key — all
 * the same outcome, because in every one of them a link or a card would lead somewhere the user
 * cannot act. `=== true` rather than truthiness, for the same reason.
 */
export interface ModuleStatus {
  /** The CRM workspace at `/crm`. */
  crmEnabled: boolean;
  /** `/status`'s `counts`, verbatim, when the CRM answered with them (2026-10-18). The home page
   *  shows the same numbers the CRM rail shows, from the same response. `null` = none sent. */
  crmCounts?: Record<string, number> | null;
  /** The brief shelf at `/briefs`, and the `/reports*` routes it reads. */
  briefsEnabled: boolean;
  /**
   * False until the probe has answered. A surface that wants to say "the shelf is switched off"
   * must not say it during the round trip — "we have not asked yet" and "the answer was no" are
   * different facts, and only one of them is worth printing.
   */
  settled: boolean;
}

export function useModuleStatus(): ModuleStatus {
  const [status, setStatus] = useState<ModuleStatus>({
    crmEnabled: false,
    briefsEnabled: false,
    settled: false,
  });

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/next/crm/status", {
      credentials: "same-origin", // the session cookie is httponly
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : { enabled: false }))
      .then((body) => {
        const counts = body?.counts;
        setStatus({
          crmEnabled: body?.enabled === true,
          briefsEnabled: body?.reports_enabled === true,
          crmCounts: counts && typeof counts === "object" ? (counts as Record<string, number>) : null,
          settled: true,
        });
      })
      .catch((error: unknown) => {
        // An aborted probe is an unmounted component, not an answer. Settling here would make a
        // torn-down surface claim the modules are off.
        if (error instanceof DOMException && error.name === "AbortError") return;
        setStatus({ crmEnabled: false, briefsEnabled: false, settled: true });
      });
    return () => controller.abort();
  }, []);

  return status;
}
